export type SignatureType = {
  src: string
  alt: string
  width: number
  height: number
}

export type TeamMemberType = {
  name: string
  role: string
  alternativeRole?: string
  emailRole?: string
  photoSrc: string
  photoAlt: string
  alternativePhotoSrc?: string
  slug?: TeamMemberSlug
  bio?: string[]
  education?: string[]
  linkedInProfileLink?: string
  signature?: SignatureType
  licenses?: string[]
}

export enum TeamMemberSlug {
  igorDemcak = 'igor-demcak',
  marianPorvaznik = 'marian-porvaznik',
  marekKrizka = 'marek-krizka',
  janBuza = 'jan-buza',
  martinaSallay = 'martina-sallay',
  humaLofca = 'huma-lofca',
  janDohnal = 'jan-dohnal',
  andrejSkok = 'andrej-skok',
  tomasOrsula = 'tomas-orsula',
  nicoleFenech = 'nicole-fenech',
  jurajCvik = 'juraj-cvik',
  rohiniPandit = 'rohini-pandit',
  martinaCsaszari = 'martina-csaszari',
  peterZuffa = 'peter-zuffa',
  peterMarcely = 'peter-marcely',
  simonaGerbocova = 'simona-gerbocova',
  patrikZakutansky = 'patrik-zakutansky',
  jakubSvatek = 'jakub-svatek',
  patrikPoprocky = 'patrik-poprocky',
  petraHobinkova = 'petra-hobinkova',
  michalPavljuk = 'michal-pavljuk',
  dariiaHorobchenko = 'dariia-horobchenko',
  mariaMiskovic = 'maria-miskovic',
  viktorStark = 'viktor-stark',
  liviaMoraes = 'livia-moraes',
  katarinaBalaskova = 'katarina-balaskova',
  katarinaJancovic = 'katarina-jancovic',
  michaelaMihok = 'michaela-mihok',
  zuzanaHavasova = 'zuzana-havasova',
  patriciaBalcova = 'patricia-balcova',
  darioFernandez = 'dario-fernandez',
  valeriaEstrada = 'valeria-estrada',
  martaSkupnik = 'marta-skupnik',
  tomasJanek = 'tomas-janek',
  peterDuris = 'peter-duris',
  michaelaFurikova = 'michaela-furikova',
  luciaVyletelkova = 'lucia-vyletelkova',
  ninaRybarova = 'nina-rybarova',
  leonardoPeres = 'leonardo-peres',
  milanTomic = 'milan-tomic',
  luciaHavasova = 'lucia-havasova',
  radkaKulkova = 'radka-kulkova',
  evamariaKorbelova = 'evamaria-korbelova',
  anetaMarcely = 'aneta-marcely',
  claudiaFernandezAreste = 'claudia-fernandez-areste',
  eduardPetrov = 'eduard-petrov',
  erikZurvalec = 'erik-zurvalec',
  katarinaKobliskova = 'katarina-kobliskova',
}

type TeamMemberEntry = Omit<TeamMemberType, 'slug'> & {
  hasProfilePage?: boolean
}

const teamMembers = {
  [TeamMemberSlug.igorDemcak]: {
    name: 'team.team-igor-demcak-name',
    role: 'team.team-role-founder-and-attorney',
    alternativeRole: 'team.team-role-director-of-legal',
    emailRole: 'team.team-role-founder-and-attorney-translated',
    photoSrc: '/images/team/igor-demcak.jpg',
    photoAlt: 'Photo of Igor Demcak',
    hasProfilePage: true,
    bio: ['team.team-igor-demcak-bio-0', 'team.team-igor-demcak-bio-1'],
    education: ['team.team-igor-demcak-education-0'],
    linkedInProfileLink: 'https://www.linkedin.com/in/igor-dem%C4%8D%C3%A1k-586ab853',
    signature: {
      src: '/images/signature/igor-demcak.png',
      alt: 'Signature of Igor Demcak',
      width: 270,
      height: 120,
    },
  },
  [TeamMemberSlug.marianPorvaznik]: {
    name: 'team.team-marian-porvaznik-name',
    role: 'team.team-role-partner-and-strategic-mind',
    photoSrc: '/images/team/majo-porvaznik.jpg',
    photoAlt: 'Photo of Marian Porvaznik',
  },
  [TeamMemberSlug.marekKrizka]: {
    name: 'team.team-marek-krizka-name',
    role: 'team.team-role-us-attorney-and-legal-mind',
    emailRole: 'team.team-role-us-trademark-attorney',
    photoSrc: '/images/team/marek-krizka.jpg',
    photoAlt: 'Photo of Marek Krizka',
    hasProfilePage: true,
    bio: ['team.team-marek-krizka-bio-0'],
    education: ['team.team-marek-krizka-education-0'],
    linkedInProfileLink: 'https://www.linkedin.com/in/marek-krizka-6243878a',
    signature: {
      src: '/images/signature/marek-krizka.png',
      alt: 'Signature of Marek Krizka',
      width: 270,
      height: 120,
    },
  },
  [TeamMemberSlug.janBuza]: {
    name: 'team.team-jan-buza-name',
    role: 'team.team-head-of-bizdev',
    photoSrc: '/images/team/jan-buza.jpg',
    photoAlt: 'Photo of Jan Buza',
  },
  [TeamMemberSlug.martinaSallay]: {
    name: 'team.team-martina-ninajova-name',
    role: 'team.team-role-head-of-legal',
    photoSrc: '/images/team/martina-ninajova.jpg',
    photoAlt: 'Photo of Martina Sallay',
    hasProfilePage: true,
    bio: ['team.team-martina-ninajova-bio-0', 'team.team-martina-ninajova-bio-1'],
    education: ['team.team-martina-ninajova-education-0', 'team.team-martina-ninajova-education-1'],
    linkedInProfileLink: 'https://www.linkedin.com/in/martina-ninajova-ll-m-2961b7134',
    alternativePhotoSrc: '/images/team/martina-ninajova-2.jpg',
  },
  [TeamMemberSlug.humaLofca]: {
    name: 'team.team-huma-lofca-name',
    role: 'team.team-role-us-attorney-and-legal-mind',
    photoSrc: '/images/team/huma-lofca-2.jpg',
    photoAlt: 'Photo of Huma Lofca',
    hasProfilePage: true,
    bio: ['team.team-huma-lofca-bio-0', 'team.team-huma-lofca-bio-1'],
    education: ['team.team-huma-lofca-education-0', 'team.team-huma-lofca-education-1'],
    linkedInProfileLink: 'https://www.linkedin.com/in/huma-lofca-esq-239613228',
    licenses: ['team.team-huma-lofca-license-0', 'team.team-huma-lofca-license-1'],
  },
  [TeamMemberSlug.janDohnal]: {
    name: 'team.team-jan-dohnal-name',
    role: 'team.team-role-legal-mind',
    emailRole: 'team.team-role-senior-trademark-lawyer',
    photoSrc: '/images/team/jan-dohnal.jpg',
    photoAlt: 'Photo of Jan Dohnal',
    hasProfilePage: true,
    bio: ['team.team-jan-dohnal-bio-0', 'team.team-jan-dohnal-bio-1'],
    education: ['team.team-jan-dohnal-education-0'],
    linkedInProfileLink: 'https://www.linkedin.com/in/jandohnalcz',
  },
  [TeamMemberSlug.andrejSkok]: {
    name: 'team.team-andrej-skok-name',
    role: 'team.team-head-of-product',
    photoSrc: '/images/team/andy-skok.jpg',
    photoAlt: 'Photo of Andrej Skok',
  },
  [TeamMemberSlug.tomasOrsula]: {
    name: 'team.team-tomas-orsula-name',
    role: 'team.team-role-legal-engagement-lead',
    emailRole: 'team.team-role-senior-trademark-lawyer',
    photoSrc: '/images/team/tomas-orsula.jpg',
    photoAlt: 'Photo of Tomas Orsula',
    hasProfilePage: true,
    bio: ['team.team-tomas-orsula-bio-0', 'team.team-tomas-orsula-bio-1'],
    education: ['team.team-tomas-orsula-education-0'],
    linkedInProfileLink: 'https://www.linkedin.com/in/tomas-orsula-ll-m-usc-12338659',
  },
  [TeamMemberSlug.nicoleFenech]: {
    name: 'team.team-nicole-fenech-name',
    role: 'team.team-role-brand-protection-consultant',
    photoSrc: '/images/team/nicole-fenech.jpg',
    photoAlt: 'Photo of Nicole Fenech',
  },
  [TeamMemberSlug.jurajCvik]: {
    name: 'team.team-juraj-cvik-name',
    role: 'team.team-role-ceo',
    alternativeRole: 'team.team-role-ceo-and-co-founder',
    photoSrc: '/images/team/juraj-cvik-2.jpg',
    photoAlt: 'Photo of Juraj Cvik',
    signature: {
      src: '/images/signature/juraj-cvik.png',
      alt: 'Signature of Juraj Cvik',
      width: 410,
      height: 120,
    },
  },
  [TeamMemberSlug.rohiniPandit]: {
    name: 'team.team-rohini-pandit-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/rohini-pandit-2.jpg',
    photoAlt: 'Photo of Rohini Pandit',
  },
  [TeamMemberSlug.martinaCsaszari]: {
    name: 'team.team-martina-csaszari-name',
    role: 'team.team-role-legal-mind',
    emailRole: 'team.team-role-trademark-specialist',
    photoSrc: '/images/team/martina-csaszari.jpg',
    photoAlt: 'Photo of Martina Csaszari',
    hasProfilePage: true,
    bio: ['team.team-martina-csaszari-bio-0', 'team.team-martina-csaszari-bio-1'],
    education: ['team.team-martina-csaszari-education-0'],
    linkedInProfileLink: 'https://www.linkedin.com/in/martina-csaszari-b00729218',
  },
  [TeamMemberSlug.peterZuffa]: {
    name: 'team.team-peter-zuffa-name',
    role: 'team.team-role-tech-mind',
    photoSrc: '/images/team/peter-zuffa-2.jpg',
    photoAlt: 'Photo of Peter Zuffa',
  },
  [TeamMemberSlug.peterMarcely]: {
    name: 'team.team-peter-marcely-name',
    role: 'team.team-role-delivery-lead',
    photoSrc: '/images/team/peter-marcely-2.jpg',
    photoAlt: 'Photo of Peter Marcely',
  },
  [TeamMemberSlug.simonaGerbocova]: {
    name: 'team.team-simona-gerbocova-name',
    role: 'team.team-role-tech-ui-mind',
    photoSrc: '/images/team/simona-gerbocova.jpg',
    photoAlt: 'Photo of Simona Gerbocova',
  },
  [TeamMemberSlug.patrikZakutansky]: {
    name: 'team.team-patrik-zakutansky-name',
    role: 'team.team-role-cto',
    photoSrc: '/images/team/patrik-zakutansky.jpg',
    photoAlt: 'Photo of Patrik Zakutansky',
  },
  [TeamMemberSlug.jakubSvatek]: {
    name: 'team.team-jakub-svatek-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/jakub-svatek.jpg',
    photoAlt: 'Photo of Jakub Svatek',
  },
  [TeamMemberSlug.patrikPoprocky]: {
    name: 'team.team-patrik-poprocky-name',
    role: 'team.team-role-legal-mind',
    emailRole: 'team.team-role-legal-operation-manager',
    photoSrc: '/images/team/patrik-poprocky.jpg',
    photoAlt: 'Photo of Patrik Poprocky',
  },
  [TeamMemberSlug.petraHobinkova]: {
    name: 'team.team-petra-cupecova-name',
    role: 'team.team-role-legal-mind',
    emailRole: 'team.team-role-trademark-lawyer',
    photoSrc: '/images/team/petra-cupecova.jpg',
    photoAlt: 'Photo of Petra Hobinkova',
  },
  [TeamMemberSlug.michalPavljuk]: {
    name: 'team.team-michal-pavljuk-name',
    role: 'team.team-bizdev-mind',
    emailRole: 'team.team-role-bizdev-representative',
    photoSrc: '/images/team/michal-pavljuk.jpg',
    photoAlt: 'Photo of Michal Pavljuk',
  },
  [TeamMemberSlug.dariiaHorobchenko]: {
    name: 'team.team-dariia-horobchenko-name',
    role: 'team.team-marketing-mind',
    photoSrc: '/images/team/dariia-horobchenko.jpg',
    photoAlt: 'Photo of Dariia Horobchenko',
  },
  [TeamMemberSlug.mariaMiskovic]: {
    name: 'team.team-maria-vajda-name',
    role: 'team.team-head-of-operations',
    photoSrc: '/images/team/maria-vajda.jpg',
    photoAlt: 'Photo of Maria Miskovic',
  },
  [TeamMemberSlug.viktorStark]: {
    name: 'team.team-viktor-stark-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/viktor-stark.jpg',
    photoAlt: 'Photo of Viktor Stark',
  },
  [TeamMemberSlug.liviaMoraes]: {
    name: 'team.team-livia-moraes-name',
    role: 'team.team-role-trademark-attorney-and-legal-mind',
    photoSrc: '/images/team/livia-moraes-2.jpg',
    photoAlt: 'Photo of Livia Moraes',
    hasProfilePage: true,
    bio: ['team.team-livia-moraes-bio-0', 'team.team-livia-moraes-bio-1'],
    education: ['team.team-livia-moraes-education-0', 'team.team-livia-moraes-education-1'],
    linkedInProfileLink: 'https://www.linkedin.com/in/liviacmoraes',
    licenses: ['team.team-livia-moraes-license-0'],
  },
  [TeamMemberSlug.katarinaBalaskova]: {
    name: 'team.team-katarina-balaskova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/katarina-balaskova-2.jpg',
    photoAlt: 'Photo of Katarina Balaskova',
  },
  [TeamMemberSlug.katarinaJancovic]: {
    name: 'team.team-katarina-jancovic-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/katarina-jancovic.jpg',
    photoAlt: 'Photo of Katarina Jancovic',
  },
  [TeamMemberSlug.michaelaMihok]: {
    name: 'team.team-michaela-bockova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/michaela-bockova-2.jpg',
    photoAlt: 'Photo of Michaela Mihok',
  },
  [TeamMemberSlug.zuzanaHavasova]: {
    name: 'team.team-zuzana-havasova-name',
    role: 'team.team-role-tester',
    photoSrc: '/images/team/zuzana-havasova-2.jpg',
    photoAlt: 'Photo of Zuzana Havasova',
  },
  [TeamMemberSlug.patriciaBalcova]: {
    name: 'team.team-patricia-balcova-name',
    role: 'team.team-role-customer-experience-specialist',
    photoSrc: '/images/team/patricia-balcova-2.jpg',
    photoAlt: 'Photo of Patricia Balcova',
  },
  [TeamMemberSlug.darioFernandez]: {
    name: 'team.team-dario-fernandez-name',
    role: 'team.team-role-brand-protection-consultant',
    photoSrc: '/images/team/dario-fernandez-2.jpg',
    photoAlt: 'Photo of Dario Fernandez',
  },
  [TeamMemberSlug.valeriaEstrada]: {
    name: 'team.team-valeria-estrada-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/valeria-estrada.jpg',
    photoAlt: 'Photo of Valeria Estrada',
  },
  [TeamMemberSlug.martaSkupnik]: {
    name: 'team.team-marta-skupnik-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/marta-skupnik.jpg',
    photoAlt: 'Photo of Marta Skupnik',
  },
  [TeamMemberSlug.tomasJanek]: {
    name: 'team.team-tomas-janek-name',
    role: 'team.team-product-design',
    photoSrc: '/images/team/tomas-janek.jpg',
    photoAlt: 'Photo of Tomas Janek',
  },
  [TeamMemberSlug.peterDuris]: {
    name: 'team.team-peter-duris-name',
    role: 'team.team-marketing-mind',
    photoSrc: '/images/team/peter-duris.jpg',
    photoAlt: 'Photo of Peter Duris',
  },
  [TeamMemberSlug.michaelaFurikova]: {
    name: 'team.team-michaela-furikova-name',
    role: 'team.team-role-operations-mind',
    photoSrc: '/images/team/michaela-furikova.jpg',
    photoAlt: 'Photo of Michaela Furikova',
  },
  [TeamMemberSlug.luciaVyletelkova]: {
    name: 'team.team-lucia-vyletelkova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/lucia-vyletelkova.jpg',
    photoAlt: 'Photo of Lucia Vyletelkova',
  },
  [TeamMemberSlug.ninaRybarova]: {
    name: 'team.team-nina-rybarova-name',
    role: 'team.team-role-operations-mind',
    photoSrc: '/images/team/nina-rybarova-2.jpg',
    photoAlt: 'Photo of Nina Rybarova',
  },
  [TeamMemberSlug.leonardoPeres]: {
    name: 'team.team-leonardo-peres-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/leonardo-peres.jpg',
    photoAlt: 'Photo of Leonardo Peres',
  },
  [TeamMemberSlug.milanTomic]: {
    name: 'team.team-milan-tomic-name',
    role: 'team.team-marketing-mind',
    photoSrc: '/images/team/milan-tomic-2.jpg',
    photoAlt: 'Photo of Milan Tomic',
  },
  [TeamMemberSlug.luciaHavasova]: {
    name: 'team.team-lucia-havasova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/lucia-havasova-2.jpg',
    photoAlt: 'Photo of Lucia Havasova',
  },
  [TeamMemberSlug.radkaKulkova]: {
    name: 'team.team-radka-kulkova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/radka-kulkova.jpg',
    photoAlt: 'Photo of Radka Kulkova',
  },
  [TeamMemberSlug.evamariaKorbelova]: {
    name: 'team.team-evamaria-korbelova-name',
    role: 'team.team-role-brand-protection-consultant',
    photoSrc: '/images/team/evamaria-korbelova-2.jpg',
    photoAlt: 'Photo of Evamaria Korbelova',
  },
  [TeamMemberSlug.anetaMarcely]: {
    name: 'team.team-aneta-marcely-name',
    role: 'team.team-role-client-success-representative',
    photoSrc: '/images/team/aneta-marcely.jpg',
    photoAlt: 'Photo of Aneta Marcely',
  },
  [TeamMemberSlug.claudiaFernandezAreste]: {
    name: 'team.team-claudia-fernandez-areste-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/claudia-fernandez-areste.jpg',
    photoAlt: 'Photo of Claudia Fernandez Areste',
  },
  [TeamMemberSlug.eduardPetrov]: {
    name: 'team.team-eduard-petrov-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/eduard-petrov.jpg',
    photoAlt: 'Photo of Eduard Petrov',
  },
  [TeamMemberSlug.erikZurvalec]: {
    name: 'team.team-erik-zurvalec-name',
    role: 'team.team-role-tech-mind',
    photoSrc: '/images/team/erik-zurvalec-2.jpg',
    photoAlt: 'Photo of Erik Zurvalec',
  },
  [TeamMemberSlug.katarinaKobliskova]: {
    name: 'team.team-katarina-kobliskova-name',
    role: 'team.team-role-legal-mind',
    photoSrc: '/images/team/katarina-kobliskova.jpg',
    photoAlt: 'Photo of Katarina Kobliskova',
  },
} satisfies Record<TeamMemberSlug, TeamMemberEntry>

export const team = Object.fromEntries(
  Object.entries<TeamMemberEntry>(teamMembers).map(([slug, {hasProfilePage, ...member}]) => [
    slug,
    hasProfilePage ? {...member, slug} : member,
  ]),
) as Record<TeamMemberSlug, TeamMemberType>

export enum TeamCategory {
  highlighted = 'Highlighted',
  legal = 'Legal',
  customerSuccess = 'Customer Success',
  product = 'Product',
  bizDev = 'Business Development',
  marketing = 'Marketing',
  operations = 'Operations',
}
